import { useCallback, useEffect, useMemo, useState } from "react";

import {
  useGetTaskBoardMediaInfoQuery,
  type TaskBoard,
} from "../../../../app/services/taskBoard";
import type { EmbeddedMediaResolver } from "../../../../components/markdown/embeddedMedia";
import { taskBoardUploadKeyOfUrl } from "../../../../components/markdown/markdownAttachment";
import { collectUploadKeys } from "./taskBoardUtils";

/** media-info 1 リクエストあたりのキー数上限（BE の MAX_MEDIA_INFO_KEYS） */
const MAX_KEYS = 500;

/** 変換中のファイルがある間の再取得間隔 */
const PENDING_POLLING_MS = 30_000;

/**
 * 変換待ちとして再取得を続ける上限時間（添付キーが変わってから）。
 * 変換に失敗して記録も残らないケースで、ボードを開いている間ずっと
 * 問い合わせ続けないためのガード。
 */
const PENDING_POLLING_LIMIT_MS = 10 * 60_000;

/** MediaConvert Lambda が変換する拡張子（index.js の VIDEO / IMAGE_EXTENSIONS と PDF） */
const CONVERTIBLE_EXTENSIONS = new Set([
  "mp4", "mov", "mkv", "avi", "webm", "mpg",
  "jpg", "jpeg", "png", "gif", "webp", "bmp", "tiff",
  "pdf",
]);

const TERMINAL_STATUSES = new Set(["COMPLETE", "FAILED"]);

const isConvertible = (key: string): boolean =>
  CONVERTIBLE_EXTENSIONS.has(key.split(".").pop()?.toLowerCase() ?? "");

/**
 * プロジェクトの全ボードの本文からセル添付のキーを集め、変換結果
 * （動画・PDF のサムネイル、画像の最適化版）を 1 回でまとめて引く。
 * 戻り値は EmbeddedMediaContext へ渡すリゾルバー。
 *
 * ボード本体の取得・同期とは独立しており、添付キーの集合が変わったときだけ
 * 引き直す。アップロード直後で変換中のファイルがあれば一定時間だけ再取得する。
 */
export function useTaskBoardMedia(
  projectId: string,
  boards: TaskBoard[],
): EmbeddedMediaResolver {
  const keys = useMemo(
    () =>
      collectUploadKeys(
        boards.flatMap((board) => board.tasks),
        projectId,
      ).slice(0, MAX_KEYS),
    [boards, projectId],
  );
  // 配列の参照ではなく内容でキャッシュキー・effect を揃える
  const keysSignature = keys.join("\n");

  const [pollingDeadline, setPollingDeadline] = useState(0);
  useEffect(() => {
    setPollingDeadline(Date.now() + PENDING_POLLING_LIMIT_MS);
  }, [keysSignature]);

  const [pollingInterval, setPollingInterval] = useState(0);
  const { data } = useGetTaskBoardMediaInfoQuery(
    { project_id: projectId, keys },
    {
      skip: !projectId || keys.length === 0,
      pollingInterval,
      skipPollingIfUnfocused: true,
    },
  );
  const items = data?.data?.items;

  useEffect(() => {
    const hasPending = keys.some(
      (key) =>
        isConvertible(key) &&
        !TERMINAL_STATUSES.has(items?.[key]?.status ?? ""),
    );
    const remaining = pollingDeadline - Date.now();
    if (!items || !hasPending || remaining <= 0) {
      setPollingInterval(0);
      return;
    }
    setPollingInterval(PENDING_POLLING_MS);
    // 上限時間に達したら止める（ポーリング結果が変わらず再評価されない場合に備える）
    const timer = window.setTimeout(() => setPollingInterval(0), remaining);
    return () => window.clearTimeout(timer);
    // keysSignature で keys の内容変化を拾う
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, keysSignature, pollingDeadline]);

  return useCallback<EmbeddedMediaResolver>(
    (url) => {
      const key = taskBoardUploadKeyOfUrl(url);
      const item = key ? items?.[key] : undefined;
      if (!item || item.status !== "COMPLETE") return undefined;
      return { thumbnailUrl: item.thumbnail_url, imageUrl: item.image_url };
    },
    [items],
  );
}
